import React, { useState, useMemo } from 'react';
import Modal, { ModalCloseButton } from './Modal';
import ConfirmDialog from './ConfirmDialog';
import StatusPicker from './StatusPicker';
import StatusHistory from './StatusHistory';
import { getColor } from '../statusColors';
import { apiFetch } from '../api';
import { getCurrentPosition, SAVE_ERROR_MSG, CONFLICT_ERROR_MSG } from '../utils';
import { enqueueOfflineSave } from '../offlineQueue';
import { celebrateClose } from '../celebration';
import { CONFLICT_REFRESHED_MSG, fetchFreshLead, newerLead } from './leadConflict';

const API = '/api/leads';

// "Ganzes Haus"-Kurzbefehl — Geclosed bewusst nicht dabei: Abschlüsse bleiben pro Einheit
const BULK_STATUSES = ['Nicht anwesend', 'Kein Interesse', 'Unstimmig', 'Interesse', 'Nicht schreibbar'];

export function parseUnitData(lead) {
  let parsed = {};
  if (lead.unit_statuses) {
    try { parsed = JSON.parse(lead.unit_statuses); } catch {}
  }
  const result = {};
  for (let i = 1; i <= lead.anzahl_ne; i++) {
    const existing = parsed[i];
    if (!existing) result[i] = { status: null, notizen: '', count: 1 };
    else if (typeof existing === 'string') result[i] = { status: existing, notizen: '', count: 1 };
    else result[i] = { status: existing.status ?? null, notizen: existing.notizen ?? '', count: existing.count ?? 1 };
  }
  return result;
}

// Vorschau wie der Server rechnet: abgeschlossene Einheiten bleiben, gleicher Status
// zählt als Wiederholungsbesuch (+1), sonst neuer Status mit Zähler 1
function bulkPreview(base, status) {
  const next = {};
  for (const [k, u] of Object.entries(base)) {
    if (u.status === 'Geclosed') { next[k] = u; continue; }
    const repeat = u.status === status && !['Kein Interesse', 'Nicht schreibbar'].includes(status);
    next[k] = { ...u, status, count: repeat ? (u.count ?? 1) + 1 : 1 };
  }
  return next;
}

const sameUnit = (a, b) => (a?.status ?? null) === (b?.status ?? null) && (a?.notizen ?? '') === (b?.notizen ?? '') && (a?.count ?? 1) === (b?.count ?? 1);

export default function UnitPickerModal({ lead: leadProp, onSaved, onOpenDetail, onClose, authHeader, user }) {
  // Nach einem 409 nachgeladener Stand — ab dann Basis für Anzeige und Speichern
  const [freshLead, setFreshLead] = useState(null);
  const lead = newerLead(freshLead, leadProp);
  const [conflict, setConflict] = useState(false);
  const [unitData, setUnitData] = useState(() => parseUnitData(lead));
  // Gespeicherter Ausgangszustand — Referenz für die "+1 pro Speicherung"-Grenze
  const baseUnitData = useMemo(() => parseUnitData(lead), [lead]);
  const [activeUnit, setActiveUnit] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Gewählter Kurzbefehl — wird als units_bulk_status gespeichert (Server: ein
  // Verlaufseintrag für alle Einheiten statt einer Zeile pro Einheit)
  const [bulkStatus, setBulkStatus] = useState(null);
  // Ziel-Status für die aktive Einheit, der einen Abschluss aufheben würde — erst nach Rückfrage
  const [pendingUnitStatus, setPendingUnitStatus] = useState(undefined);

  const applyUnitStatus = v => setUnitData(prev => ({ ...prev, [activeUnit]: { ...prev[activeUnit], status: v, count: 1 } }));
  const selectUnitStatus = v => {
    if ((unitData[activeUnit]?.status ?? null) === 'Geclosed' && v !== 'Geclosed') { setPendingUnitStatus(v); return; }
    applyUnitStatus(v);
  };

  const toggleBulk = status => {
    if (bulkStatus === status) { setBulkStatus(null); setUnitData(parseUnitData(lead)); return; }
    setBulkStatus(status);
    setUnitData(bulkPreview(baseUnitData, status));
  };

  const addr = [lead.strasse, lead.hausnummer, lead.zusatz].filter(Boolean).join(' ');
  const closedCount = Object.values(unitData).filter(u => u.status === 'Geclosed').length;
  const current = unitData[activeUnit] ?? { status: null, notizen: '' };

  const updateCurrent = (field, value) =>
    setUnitData(prev => ({ ...prev, [activeUnit]: { ...prev[activeUnit], [field]: value } }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setConflict(false);

    // Herausfinden welche Einheiten JETZT NEU auf Geclosed gesetzt werden — außerhalb
    // des try, damit GPS-Erfassung auch im Offline-Queue-Fallback (catch) erhalten bleibt
    const originalData = parseUnitData(lead);
    const newlyClosedUnits = bulkStatus ? [] : Object.keys(unitData).filter(k =>
      unitData[k].status === 'Geclosed' && originalData[k]?.status !== 'Geclosed'
    );

    // GPS holen wenn mindestens eine Einheit neu geclosed wird
    let pos = null;
    if (newlyClosedUnits.length > 0) {
      pos = await getCurrentPosition();
    }

    // Tracking-Daten in neu geschlossene Einheiten einbetten
    const now = new Date().toISOString();
    const enriched = { ...unitData };
    for (const k of newlyClosedUnits) {
      if (!enriched[k].geclosed_by) {   // nicht überschreiben falls bereits gesetzt
        enriched[k] = {
          ...enriched[k],
          geclosed_by:  user?.username ?? null,
          geclosed_at:  now,
          geclosed_lat: pos?.lat ?? null,
          geclosed_lng: pos?.lng ?? null,
        };
      }
    }
    const body = bulkStatus
      ? { ...lead, units_bulk_status: bulkStatus }
      : { ...lead, unit_statuses: JSON.stringify(enriched) };

    try {
      const res = await apiFetch(`${API}/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(body),
      });
      if (res.status === 409) {
        const fresh = await fetchFreshLead(lead.id, authHeader);
        if (!fresh) { setError(CONFLICT_ERROR_MSG); return; }
        // Nicht angefasste Einheiten auf den neuen Stand ziehen, eigene Eingaben behalten
        const freshUnits = parseUnitData(fresh);
        if (bulkStatus) setUnitData(bulkPreview(freshUnits, bulkStatus));
        else {
          const merged = {};
          for (const k of Object.keys(freshUnits)) {
            merged[k] = unitData[k] && !sameUnit(unitData[k], originalData[k]) ? unitData[k] : freshUnits[k];
          }
          setUnitData(merged);
        }
        setFreshLead(fresh);
        setConflict(true);
        setError(CONFLICT_REFRESHED_MSG);
        return;
      }
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      if (newlyClosedUnits.length > 0) celebrateClose(newlyClosedUnits.length);
      onSaved(await res.json());
    } catch (err) {
      // fetch() wirft TypeError nur bei echtem Netzwerkausfall (kein Server erreichbar) —
      // dann lokal übernehmen und automatisch synchronisieren, sobald wieder Verbindung da ist.
      if (err instanceof TypeError) {
        enqueueOfflineSave(user?.username, lead.id, body);
        if (newlyClosedUnits.length > 0) celebrateClose(newlyClosedUnits.length);
        onSaved({ ...body, _pendingSync: true });
        return;
      }
      console.error('Fehler beim Speichern:', err);
      setError(SAVE_ERROR_MSG);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} label={`Einheiten — ${addr}`} style={{ maxWidth: 780, width: '95vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h2>{addr}</h2>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{lead.anzahl_ne} Einheiten · {lead.ort}</span>
          </div>
          <ModalCloseButton onClose={onClose} />
        </div>

        {closedCount > 0 && (
          <div style={{ background: '#dcfce7', borderRadius: 8, padding: '6px 14px', marginBottom: 12, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
            {closedCount} von {lead.anzahl_ne} Einheiten Geclosed
          </div>
        )}

        {/* Ganzes Haus: ein Status für alle offenen Einheiten (z.B. niemand hat aufgemacht) */}
        <div style={{ marginBottom: 14, padding: '10px 12px', background: '#f9f8ff', border: '1px solid #ede9fe', borderRadius: 10 }}>
          <div className="section-label" style={{ marginBottom: 6 }}>Ganzes Haus</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {BULK_STATUSES.map(s => {
              const color = getColor(s);
              const active = bulkStatus === s;
              return (
                <button key={s} type="button" onClick={() => toggleBulk(s)} aria-pressed={active} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', minHeight: 36,
                  borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                  border: `1.5px solid ${active ? color : color + '66'}`,
                  background: active ? color : 'white', color: active ? 'white' : color,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? 'white' : color }} />
                  {s}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
            {bulkStatus
              ? `Alle nicht abgeschlossenen Einheiten werden beim Speichern auf „${bulkStatus}“ gesetzt — zum Einzelbearbeiten Kurzbefehl abwählen.`
              : 'Setzt alle nicht abgeschlossenen Einheiten auf einmal. Abschlüsse bitte pro Einheit.'}
          </div>
        </div>

        {/* Lead-Felder */}
        <div className="detail-grid" style={{ marginBottom: 14 }}>
          <div className="detail-item"><span>Kategorie</span><strong>{lead.kategorie}</strong></div>
          <div className="detail-item"><span>Internet</span><strong>{lead.internet_verfuegbar}</strong></div>
          <div className="detail-item"><span>GEE liegt vor</span><strong>{lead.gee_liegt_vor}</strong></div>
          <div className="detail-item"><span>SV-Potenzial</span><strong>{lead.sv_potenzial}</strong></div>
          <div className="detail-item"><span>Gebäudetyp</span><strong>{lead.gebaeude_typ}</strong></div>
          <div className="detail-item"><span>HA-ID</span><strong>{lead.ha_id}</strong></div>
          {lead.mfh_eigentuemer && <div className="detail-item full"><span>Eigentümer</span><strong>{lead.mfh_eigentuemer}</strong></div>}
          {lead.kundennamen_sv && <div className="detail-item full"><span>Kunden mit SV</span><strong>{lead.kundennamen_sv}</strong></div>}
          {lead.rabattcode && <div className="detail-item"><span>Rabattcode</span><strong>{lead.rabattcode}</strong></div>}
          {lead.rabattcode_gueltig_bis && <div className="detail-item"><span>Gültig bis</span><strong>{lead.rabattcode_gueltig_bis}</strong></div>}
          {lead.montage_termin && <div className="detail-item full"><span>Montagetermin</span><strong>{lead.montage_termin}</strong></div>}
        </div>

        <div className="unit-modal-cols" style={{ display: 'flex', gap: 16, minHeight: 300 }}>
          {/* Linke Spalte: Einheitenliste (nur Ansicht) */}
          <div className="unit-modal-list" style={{ width: 120, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto', maxHeight: 380 }}>
            {Array.from({ length: lead.anzahl_ne }, (_, i) => i + 1).map(unit => {
              const status = unitData[unit]?.status ?? null;
              const color = getColor(status);
              const isActive = activeUnit === unit;
              return (
                <button key={unit} onClick={() => setActiveUnit(unit)} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                  borderRadius: 8, border: `1.5px solid ${isActive ? color : color + '55'}`,
                  background: isActive ? color + '18' : '#f9fafb',
                  cursor: 'pointer', fontWeight: isActive ? 700 : 500, fontSize: 13,
                  color: isActive ? color : '#374151', textAlign: 'left',
                }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  NE {unit}
                </button>
              );
            })}
          </div>

          <div className="unit-modal-divider" style={{ width: 1, background: '#e5e7eb', flexShrink: 0 }} />

          {/* Rechte Spalte: Detailansicht der gewählten Einheit */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, opacity: bulkStatus ? 0.45 : 1, pointerEvents: bulkStatus ? 'none' : 'auto' }}>
            <div>
              <div className="section-label">Status — NE {activeUnit}</div>
              <StatusPicker
                current={current.status}
                count={current.count ?? 1}
                onSelect={selectUnitStatus}
                onCountIncrement={() => {
                  // Pro Speicherung höchstens +1 über dem gespeicherten Stand (Server erzwingt das ebenfalls)
                  const base = baseUnitData[activeUnit];
                  const max = current.status === (base?.status ?? null) ? (base?.count ?? 1) + 1 : 1;
                  updateCurrent('count', Math.min((current.count ?? 1) + 1, max));
                }}
              />
            </div>
            <div className="form-field">
              <label>Kommentar — NE {activeUnit}</label>
              <textarea
                rows={3}
                value={current.notizen}
                onChange={e => updateCurrent('notizen', e.target.value)}
                placeholder={`Notizen für Einheit ${activeUnit}...`}
              />
            </div>
            <StatusHistory leadId={lead.id} authHeader={authHeader} unitNum={activeUnit} />
          </div>
        </div>

        {error && (
          <div className="login-error" role="alert" style={{ marginTop: 12 }}>{error}</div>
        )}

        <div className="form-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onOpenDetail}>Gebäudedetails</button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button
            type="button"
            className={`btn btn-primary${saving ? ' btn-loading' : ''}`}
            onClick={handleSave}
            disabled={saving}
            aria-busy={saving}
          >
            {saving ? 'Speichern...' : conflict ? 'Erneut speichern' : error ? 'Erneut versuchen' : 'Speichern'}
          </button>
        </div>
        {pendingUnitStatus !== undefined && (
          <ConfirmDialog
            title={`Abschluss von NE ${activeUnit} aufheben?`}
            message={`Die Einheit wird auf „${pendingUnitStatus ?? 'Nicht bearbeitet'}“ gesetzt. Datum, Standort und Zuordnung des Abschlusses gehen dabei verloren und er zählt nicht mehr in der Statistik — der Verlauf bleibt erhalten.`}
            confirmLabel="Abschluss aufheben"
            onConfirm={() => { applyUnitStatus(pendingUnitStatus); setPendingUnitStatus(undefined); }}
            onCancel={() => setPendingUnitStatus(undefined)}
          />
        )}
    </Modal>
  );
}
